"use client";

import { useRef, useState, useEffect, startTransition } from "react";
import { useSession, signIn } from "@/lib/auth/client";
import { useRouter } from "next/navigation";
import { useCredits } from "@/hooks/use-credits";
import {
  canUseFreePreviewForUser,
  freePreviewEligibilityFromCredits,
} from "@/lib/payments/free-preview-config";

export function useLandingCta() {
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const { data: session } = useSession();
  const { data: credits } = useCredits();
  const router = useRouter();

  // Avoid hydration mismatches by keeping initial SSR render deterministic.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    startTransition(() => {
      setIsMounted(true);
    });
  }, []);

  const isAuthenticated = isMounted ? !!session?.user : false;

  const canUseFreePreview =
    isAuthenticated &&
    canUseFreePreviewForUser(freePreviewEligibilityFromCredits(credits));

  // Show "Go to App" if user is authenticated and cannot use free preview
  const shouldShowGoToApp = isAuthenticated && !canUseFreePreview;
  
  // Single source of truth for CTA text
  const ctaText = shouldShowGoToApp ? "Go to App" : "Start for free";

  // Intersection Observer to detect when hero CTA is not visible
  useEffect(() => {
    if (!heroCtaRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setShowStickyCta(!entries[0].isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: "0px",
      }
    );

    observer.observe(heroCtaRef.current);

    return () => {
      if (heroCtaRef.current) {
        observer.unobserve(heroCtaRef.current);
      }
    };
  }, []);

  const handleTryItNow = () => {
    if (session?.user) {
      router.push("/app");
    } else {
      signIn.social({
        provider: "google",
        callbackURL: "/app",
      });
    }
  };

  return {
    heroCtaRef,
    showStickyCta,
    ctaText,
    handleTryItNow,
    isAuthenticated,
    canUseFreePreview,
  };
}
